// Implements: F-GEN-033, F-GEN-034, F-UIX-025, F-UIX-009
/**
 * The `diagrams` module's client (mockup v3 P6, contract C6.1 and C6.2).
 *
 * A version holds two diagrams, each drawn once: the journey and the screen walkthrough. Every
 * call names the `kind` it is about. `estimateDiagram` is read-only: it runs nothing and charges
 * nothing. `runDiagram` is a paid, non-idempotent call, so it is never retried by the HTTP client
 * (checklist 13). A refusal to draw is a `409` whose public `reason` `refusalOf` reads.
 */

import { ApiError } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type {
  DiagramCostEstimate,
  DiagramRefusalReason,
  DiagramRunView,
  EstimateDiagramRequest,
  RunDiagramRequest,
} from "../../../types/diagrams";

function base(mockupId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/diagrams`;
}

/** `POST .../diagrams/estimate` (C6.2): the price ceiling of drawing one kind. Free. */
export async function estimateDiagram(
  mockupId: string,
  body: EstimateDiagramRequest,
  signal?: AbortSignal,
): Promise<DiagramCostEstimate | undefined> {
  return httpRequest<DiagramCostEstimate>(`${base(mockupId)}/estimate`, {
    method: "POST",
    body: { brief: body.brief, kind: body.kind },
    signal,
  });
}

/** `POST .../diagrams` (C6.1): draws one kind on the mockup's current version. Paid: never retried. */
export async function runDiagram(
  mockupId: string,
  body: RunDiagramRequest,
  signal?: AbortSignal,
): Promise<DiagramRunView | undefined> {
  const sent: RunDiagramRequest =
    body.generation_id === undefined
      ? { brief: body.brief, kind: body.kind }
      : { brief: body.brief, kind: body.kind, generation_id: body.generation_id };
  return httpRequest<DiagramRunView>(base(mockupId), { method: "POST", body: sent, signal, retry: false });
}

const REFUSALS: ReadonlySet<string> = new Set<DiagramRefusalReason>([
  "no_version",
  "diagram_already_drawn",
  "diagram_in_progress",
  "journey_required",
]);

/** The public reason of a draw (or estimate) refusal; `null` for anything that is not one. */
export function refusalOf(error: unknown): DiagramRefusalReason | null {
  if (!(error instanceof ApiError) || error.status !== 409 || error.reason === null) return null;
  return REFUSALS.has(error.reason) ? (error.reason as DiagramRefusalReason) : null;
}

